import * as React from 'react';
import { GeneralSettingsContext } from '@app/Settings/General/GeneralSettings';
import {
  PageSection,
  Card,
  CardHeader,
  CardBody,
  Button,
  Split,
  Title,
  SplitItem,
  Content,
  Tabs,
  Tab,
  TabTitleText,
  Tooltip,
} from '@patternfly/react-core';
import { Link } from 'react-router-dom';
import { sortable, headerCol, cellWidth } from '@patternfly/react-table';
import { GetResourcesResponse } from '@mergetb/api/portal/v1/realize_types';
import { GetFacilitiesResponse } from '@mergetb/api/portal/v1/commission_types';
import { InfoCircleIcon } from '@patternfly/react-icons';
import { sortable, headerCol, cellWidth, Table, Tr, Tbody, Td } from '@patternfly/react-table';
import { Allocation, GetResourcesResponse } from '@mergetb/api/portal/v1/realize_types';
import { GetUsersResponse, User as PortalUser } from '@mergetb/api/portal/v1/workspace_types';
import { ActionList } from '@app/lib/ActionList';
import { useFetch } from 'use-http';
import '@patternfly/react-core/dist/styles/base.css';
import { ChartDonut, ChartThemeColor } from '@patternfly/react-charts';

const Resources: React.FunctionComponent = () => {
  const resourceview_last = (localStorage.getItem('resourceview') ?? 'true') === 'true';
  const conf = React.useContext(GeneralSettingsContext);
  const [viewLabel, setViewLabel] = React.useState('View ' + (resourceview_last ? 'All' : 'Only Available'));
  const [resourceView, setResourceView] = React.useState(resourceview_last);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [noResults, setNoResults] = React.useState(false);
  const [facTabs, setFacTabs] = React.useState<string[] | null>([]);
  const [activeTabKey, setActiveTabKey] = React.useState('');
  const [currentResources, setCurrentResources] = React.useState({
    cores: 0,
    memory: 0,
    freeCores: 0,
    freeMemory: 0,
  });
  const [allResources, setAllResources] = React.useState({
    cores: 0,
    memory: 0,
    freeCores: 0,
    freeMemory: 0,
    numOfResources: [],
  });
  const [processedData, setProcessedData] = React.useState<GetResourcesResponse | null>(null);

  const options = { credentials: 'include', cachePolicy: 'no-cache' };
  const { loading, error, get, response } = useFetch(conf.api + '/realize/resources', options, []);
  const [resourcesData, setResourcesData] = React.useState(null);

  // load resources data. this is slow.
  const { loading, error, get, response } = useFetch(conf.api + '/realize/resources', options, []);
  const [resourcesData, setResourcesData] = React.useState(undefined);
  const {
    loading: resourcesLoading,
    error: resourcesError,
    get: getResources,
    response: resourcesResponse,
  } = useFetch(conf.api + '/realize/resources', options, []);

  const {
    loading: usersLoading,
    error: usersError,
    get: getUsers,
    response: usersResponse,
  } = useFetch(conf.api + '/users', options, []);

  // Index 0: numExperiments
  // Index 1: numProjects
  // Index 2: numUsers
  const [sphereOverview, setSphereOverview] = React.useState<number[] | null>(null);

  // facility names
  // Load these first as the call is fast. Use this to populate the "loading" state.
  const { get: facGet, response: facResp } = useFetch(conf.api + '/commission/facilities', options, []);

  const columns = [
    { title: 'Facility', key: 'facility', cellTransforms: [headerCol()], sortable: true },
    { title: 'Resource', key: 'resource', cellTransforms: [headerCol()], sortable: true },
    { title: 'Allocation Mode', key: 'allocation_mode', sortable: true },
    { title: 'Experiment Nodes', key: 'experiment_nodes', sortable: true },
    { title: 'Free Cores', key: 'free_cores', sortable: true },
    { title: 'Free Memory', key: 'free_memory', sortable: true },
  ];

  // API call for the resources.
  const load = React.useCallback(async () => {
    await getResources();
    if (resourcesResponse.ok) {
      resourcesResponse.json().then((json) => {
        setResourcesData(json);
      });
    } else {
      // setLoadError(resp);
    }
  }, [getResources, resourcesResponse]);

  // API call for the users (used to get total experiments).
  // Need a list of realizations so that they can be used to aid counting total resources.
  const fetchUsers = async (listOfReal) => {
    try {
      // Make the API call.
      await getUsers();
      // Check if it was successful.
      if (usersResponse.ok) {
        const json = await usersResponse.json();
        const users = GetUsersResponse.fromJSON(json);

        // Users are acquired. Creating some counters for the statistics that will be generated.
        let numExp = 0;
        let numProjects = 0;
        // We will count the number of unique projects across all users.
        let projects = [];
        let numUsers = Object.keys(users.users).length;

        // Iterate through each user so that we can obtain the variables above.
        users.users.forEach((user) => {
          // If any value below is undefined, treat it as zero or an empty Object.
          numExp += user?.experiments?.length || 0;
          numProjects += Object.keys(user?.projects || {}).length;

          // Used to count the number of unique projects.
          Object.keys(user?.projects || {}).forEach((projectName) => {
            // Some users may be part of the same project. Only add if it's a unique project.
            if (!projects.includes(projectName)) {
              projects.push(projectName);
            }
          });
        });

        // Once complete, call the set hook to save the parameters when generating statistics.
        setSphereOverview([numExp, projects.length, numUsers, listOfReal]);
      }
    } catch (error) {
      console.log('Error fetching users:', error);
    }
  };

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    // Do not refresh these values if we switch tabs or we have some undefined values.
    if (activeTabKey !== 'All Facilities' || !processedData || !facTabs || facTabs.length === 0) return;

    // Track unique experiments per facility.
    const expNodesByFacility = Object.fromEntries(facTabs.map((f) => [f, new Set()]));

    // Iterate through all the experiments.
    processedData.resources.forEach((r) => {
      // Get the current facility and all the nodes (after compressing ranges).
      const facility = r.resource?.facility || '';
      const expNodes = compressNodes(r.allocated);

      // For each of those, we will get the "base name" of it, which is just the name w/o nodes.
      expNodes.forEach((exp) => {
        const baseExpName = exp.replace(/\[.*?\]/g, '');
        // Add it to the set.
        expNodesByFacility[facility]?.add(baseExpName);
      });
    });

    // Convert all the experiments into their sizes.
    const allExpNodes = Object.fromEntries(
      Object.entries(expNodesByFacility).map(([facility, expSet]) => [facility, expSet.size])
    );

    // Sending it to the "fetchUsers" function.
    fetchUsers(allExpNodes);
  }, [activeTabKey, processedData, facTabs]);

  const facLoadData = React.useCallback(async () => {
    await facGet();
    if (facResp.ok) {
      facResp.json().then((json) => {
        // load just tab headings so the user sees something other than spinners
        const facNames: string[] = ['All Facilities'];
        GetFacilitiesResponse.fromJSON(json).facilities.forEach((fac) => facNames.push(fac.name));
        console.log('facNames', facNames);
        setFacTabs(facNames);
      });
    } else {
      // setLoadError(resp);
    }
  }, [facGet, facResp]);

  React.useEffect(() => {
    facLoadData();
  }, [facLoadData]);

  const toggleView = () => {
    const newView = !resourceView;
    localStorage.setItem('resourceview', newView.toString());
    setViewLabel('View ' + (newView ? 'All' : 'Only Available'));
    setResourceView(newView);
  };

  const handleSearchResults = (hasResults: boolean) => {
    setNoResults(!hasResults);
  };

  // Creates the header.
  const header = (
    <PageSection hasBodyWrapper={false}>
      <Split>
        <SplitItem>
          <Title headingLevel="h1" size="lg">
            Testbed Resources
          </Title>
        </SplitItem>
        <SplitItem isFilled />
        <SplitItem>
          <Button variant="control" aria-label={viewLabel} onClick={toggleView}>
            {viewLabel}
          </Button>
        </SplitItem>
      </Split>
    </PageSection>
  );

  // Generates the tabs.
  const tabs = (rs) => {
    // First tab will always be "All Facilities".
    let facilityTabs = ['All Facilities'];

    // Iterate through the resources and add all unique facility names.
    rs.resources.forEach((r) => {
      let facilityName = r.resource?.facility;
      if (facilityName && !facilityTabs.includes(facilityName)) {
        facilityTabs.push(facilityName);
      }
    });

    // Sending them to the "facility tab" hook so that they can be referred to later.
    setFacTabs(facilityTabs);

    // This will just default the current tab to "All Facilities" right away.
    if (!activeTabKey && facilityTabs.length > 0) {
      setActiveTabKey(facilityTabs[0]);
    }
  };

  // This is similar to the mapper variable below, but it's just called once to compute resources
  // across all facilities.
  const gatherAllResources = (rs: GetResourcesResponse) => {
    let totalCores = 0;
    let totalMem = 0;
    let totalFreeCores = 0;
    let totalFreeMem = 0;
    let numOfResourcesPerFacility = [];

    rs.resources.forEach((r) => {
      let freeCores = 0;
      let freeMem = 0;

      let resourceCores = 0;
      let resourceMem = 0;
      let usedCores = 0;
      let usedMem = 0;

      r.resource?.procs?.forEach((p) => {
        resourceCores += p.cores;
        totalCores += p.cores;
      });

      r.resource?.memory?.forEach((m) => {
        resourceMem += m.capacity;
        totalMem += m.capacity;
      });

      r.allocated.forEach((a) => {
        usedCores += a.coresUsed;
        usedMem += a.memoryUsed;
      });

      freeCores = resourceCores - usedCores;
      freeMem = resourceMem - usedMem;
      totalFreeCores += freeCores;
      totalFreeMem += freeMem;

      // Now that the resources are calculated, we will tally the resource to its corresponding facility.
      const facilityName = r?.resource?.facility;
      if (facilityName) {
        const existing = numOfResourcesPerFacility.find((obj) => obj.facility === facilityName);

        if (existing) {
          existing.count += 1;
        } else {
          numOfResourcesPerFacility.push({ facility: facilityName, count: 1 });
        }
      }
    });

    // Setting "all" resources so that they're used across all tabs.
    setAllResources({
      cores: totalCores,
      memory: totalMem,
      freeCores: totalFreeCores,
      freeMemory: totalFreeMem,
      numOfResources: numOfResourcesPerFacility,
    });
  };

  // Listener for changing the tab.
  const handleTabClick = (event: any, tabIndex: React.SetStateAction<string>) => {
    setActiveTabKey(tabIndex);
  };

  // For calculating the experiment overview. Returns an array.
  const experimentOverview = () => {
    // The total number of experiments are already calculated. Create a breakdown now.
    // We're going to create a list of objects that carry the number of experiments per facility.
    if (!facTabs || !resourcesData) return [];

    // Defining the shape of the array that we will create.
    type ExpCounterItem = {
      row: string;
      count: number;
    };

    // Assigning expCounter as an array with type ExpCounterItem above.
    // Mapping all facility tabs to it, so that we generate this initial list.
    let expCounter: ExpCounterItem[] = facTabs.map((row) => ({
      row,
      count: 0,
    }));

    // Adding two more rows for the users and projects.
    expCounter.push({
      row: 'Users',
      count: 0,
    });

    expCounter.push({
      row: 'Projects',
      count: 0,
    });

    // Find the item and update the 'row' key if it exists
    const itemToUpdate = expCounter.find((item) => item.row === 'All Facilities');

    // Replace "All Facilities" with "Reservations"
    if (itemToUpdate) {
      itemToUpdate.row = 'Reservations';
    }

    // Now, filtering each of them.
    Object.entries(sphereOverview[3]).forEach(([facName, allocatedExpCount]) => {
      const matchingFac = expCounter.find((item) => item.row === facName);
      if (matchingFac) {
        matchingFac.count += allocatedExpCount as number;
      }

      expCounter.find((item) => item.row === 'Reservations')!.count += allocatedExpCount as number;
    });

    // Assigning the users and projects, which would already be assigned by now.
    expCounter.find((item) => item.row === 'Projects')!.count = sphereOverview[1];
    expCounter.find((item) => item.row === 'Users')!.count = sphereOverview[2];
    return expCounter;
  };

  // Variable is used to generate the resource graphs for the page.
  const createGraphs = (
    currentResources: { cores?: number; memory?: number; freeCores?: number; freeMemory?: number; totalCores?: any },
    allResources: { cores: number; memory: number; freeCores: number; freeMemory: number; numOfResources: any[] },
    activeTabKey: string
  ) => {
    // Obtain the total amount of resources across all facilities. This is required for all tabs.
    let allCores = allResources.cores;
    let allMemory = allResources.memory;

    // NOTE: If we're selecting a certain tab, we will label it as "current", and gather their values.
    // This also means that if activeTabKey === "All Facilities", then allResources.freeCores === currentResources.freeCores.
    let allCurrentCores = currentResources.cores === undefined ? 0 : currentResources.cores;
    let allCurrentMemory = currentResources.memory === undefined ? 0 : currentResources.memory;
    let allCurrentFreeCores = currentResources.freeCores === undefined ? 0 : currentResources.freeCores;
    let allCurrentFreeMemory = currentResources.freeMemory === undefined ? 0 : currentResources.freeMemory;

    // Calculating percentages.
    let coresUsed = ((allCores - allCurrentFreeCores) / allCores) * 100;
    let memUsed = ((allMemory - allCurrentFreeMemory) / allMemory) * 100;

    // Some safety checks.
    coresUsed = isNaN(coresUsed) ? 0 : coresUsed;
    memUsed = isNaN(memUsed) ? 0 : memUsed;

    // Getting the number of resources per facility.
    const totalNumOfResources = allResources.numOfResources.reduce((sum, obj) => sum + obj.count, 0);

    // This is to create the data on the resource page.
    const chartData = allResources.numOfResources.map((item) => ({
      x: item.facility,
      y: item.count,
    }));

    // For creating the legend.
    const legendData = allResources.numOfResources.map((item) => ({
      name: `${item.facility}`,
    }));

    // Checking to see if "All Facilities" is selected.
    // Note: The "currentResources" parameter is redundant when this is called.
    if (activeTabKey === 'All Facilities') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', overflow: 'wrap', flexWrap: 'wrap' }}>
          {/* Total Cores: */}
          <div style={{ marginBottom: '25px' }}>
            <Content
              component="p"
              style={{ fontWeight: 'bold', margin: 'auto', textAlign: 'center', paddingRight: '25px' }}
            >
              Cores Used
            </Content>
            <ChartDonut
              ariaDesc="Core Capacity"
              ariaTitle="Cores"
              constrainToVisibleArea
              data={[
                { x: 'Used', y: coresUsed },
                { x: 'Free', y: 100 - coresUsed },
              ]}
              height={175}
              labels={({ datum }) => `${datum.x}: ${datum.y.toFixed(2)}%`}
              legendData={[
                { name: `Used: ${coresUsed.toFixed(2)}%` },
                { name: `Free: ${(100 - coresUsed).toFixed(2)}%` },
              ]}
              legendPosition="bottom"
              padding={{
                bottom: 45,
                left: 20,
                right: 45,
              }}
              subTitle={`out of \n${allCores} cores`}
              subTitlePosition="right"
              title={`${allCores - allCurrentFreeCores}`}
              width={325}
            />
          </div>

          {/* Total Memory: */}
          <div style={{ marginBottom: '25px' }}>
            <Content
              component="p"
              style={{ fontWeight: 'bold', margin: 'auto', textAlign: 'center', paddingRight: '25px' }}
            >
              Memory Used
            </Content>
            <ChartDonut
              ariaDesc="Memory Capacity"
              ariaTitle="Memory"
              constrainToVisibleArea
              data={[
                { x: 'Used', y: memUsed },
                { x: 'Free', y: 100 - memUsed },
              ]}
              height={175}
              labels={({ datum }) => `${datum.x}: ${datum.y.toFixed(2)}%`}
              legendData={[{ name: `Used: ${memUsed.toFixed(2)}%` }, { name: `Free: ${(100 - memUsed).toFixed(2)}%` }]}
              legendPosition="bottom"
              padding={{
                bottom: 45,
                left: 20,
                right: 45,
              }}
              subTitle={`out of \n${formatBytes(allMemory)}`}
              subTitlePosition="right"
              title={`${formatBytes(allMemory - allCurrentFreeMemory)}`}
              width={325}
            />
          </div>

          {/* Number of resources per facility. */}
          {/* This is just for the "All Facilities" tab. */}
          <div style={{ marginBottom: '25px' }}>
            <Content
              component="p"
              style={{ fontWeight: 'bold', margin: 'auto', textAlign: 'center', paddingRight: '25px' }}
            >
              Total Resources
            </Content>
            <ChartDonut
              ariaDesc="Number of Resources Per Facility"
              ariaTitle="Resources Per Facility"
              constrainToVisibleArea
              data={chartData}
              height={175}
              labels={({ datum }) => `${datum.x}: ${datum.y}`}
              legendData={legendData}
              legendOrientation="vertical"
              legendPosition="right"
              name="resources"
              padding={{
                bottom: 45,
                right: 130, // Legend is being added to the right.
              }}
              subTitle="resources"
              title={`${totalNumOfResources}`}
              themeColor={ChartThemeColor.multiOrdered}
              width={325}
            />
          </div>

          {/* Activity Overview: */}
          <div style={{ padding: '10px 15px 15px', border: '2px solid black', width: '275px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Content component="p" style={{ fontWeight: 'bold', marginRight: '8px', margin: '0px 5px 5px' }}>
                Activity Overview
              </Content>
              <Tooltip
                content={
                  <Content component="p" style={{ color: 'white' }}>
                    Experiments, users, and projects marked as "private" or "protected" are excluded from the overall
                    count.
                  </Content>
                }
              >
                <InfoCircleIcon style={{ display: 'inline', marginBottom: '10px' }} />
              </Tooltip>
            </div>

            <hr style={{ marginBottom: '2px' }} />
            <Table aria-label="Activity Overview" style={{ justifyContent: 'center' }}>
              <Tbody>
                {!sphereOverview ? (
                  <Tr style={{ marginTop: '2px' }}>Calculating...</Tr>
                ) : (
                  <>
                    <Tr style={{ justifyContent: 'space-between', fontWeight: 'bold', padding: '0' }}>
                      <Td style={{ padding: '0' }}>Total Experiments:</Td>
                      <Td style={{ padding: '0' }}>{sphereOverview[0]}</Td>
                    </Tr>

                    {experimentOverview()?.map((key, index) => {
                      const isReservation = key.row === 'Reservations';
                      const isUserOrProject = key.row === 'Users' || key.row === 'Projects';

                      return (
                        <Tr
                          key={index}
                          style={{
                            justifyContent: 'space-between',
                            background: isReservation ? 'yellow' : undefined,
                          }}
                        >
                          <Td style={{ padding: '0', paddingLeft: isUserOrProject || isReservation ? '0' : '10px' }}>
                            {isUserOrProject || isReservation ? `${key.row}:` : `- ${key.row}:`}
                          </Td>
                          <Td style={{ padding: '0' }}>{key.count}</Td>
                        </Tr>
                      );
                    })}
                  </>
                )}
              </Tbody>
            </Table>
          </div>
        </div>
      );
    }

    // Otherwise, render the tab that is not "All Facilities".
    // Checking that activeTabKey !== "" so that it doesn't show a blank/broken ChartDonut when loading.
    else if (activeTabKey !== '') {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', overflow: 'wrap', flexWrap: 'wrap' }}>
          {/* Total Cores: */}
          <div style={{ marginBottom: '25px' }}>
            <Content
              component="p"
              style={{ fontWeight: 'bold', margin: 'auto', textAlign: 'center', paddingRight: '25px' }}
            >
              Cores Used ({activeTabKey})
            </Content>
            <ChartDonut
              ariaDesc="Core Capacity"
              ariaTitle="Cores"
              constrainToVisibleArea
              colorScale={['#FF0000', '#4cb140', '#000000']}
              data={[
                { x: 'In Use', y: allCurrentCores - allCurrentFreeCores },
                { x: 'Available', y: allCurrentFreeCores },
              ]}
              height={200}
              labels={({ datum }) => `${datum.x}: ${datum.y}`}
              legendData={[{ name: `In Use` }, { name: `Available` }]}
              legendPosition="bottom"
              padding={{
                bottom: 45,
                left: 20,
                right: 45,
              }}
              subTitle={`${allCurrentFreeCores} cores\navailable`}
              subTitlePosition="right"
              title={`${allCurrentCores - allCurrentFreeCores}`}
              width={350}
            />
          </div>

          {/* Total Memory: */}
          <div style={{ marginBottom: '25px' }}>
            <Content component="p" style={{ fontWeight: 'bold', margin: 'auto', textAlign: 'center' }}>
              Memory Used ({activeTabKey})
            </Content>
            <ChartDonut
              ariaDesc="Memory Capacity"
              ariaTitle="Memory"
              constrainToVisibleArea
              colorScale={['#FF0000', '#4cb140', '#000000']}
              data={[
                { x: 'In Use', y: allCurrentMemory - allCurrentFreeMemory },
                { x: 'Available', y: allCurrentFreeMemory },
              ]}
              height={200}
              labels={({ datum }) => `${datum.x}: ${formatBytes(datum.y)}`}
              legendData={[{ name: `In Use` }, { name: `Available` }]}
              legendPosition="bottom"
              padding={{
                bottom: 45,
                left: 20,
                right: 45,
              }}
              subTitle={`${formatBytes(allCurrentFreeMemory)}\navailable`}
              subTitlePosition="right"
              title={`${formatBytes(allCurrentMemory - allCurrentFreeMemory)}`}
              width={350}
            />
          </div>
        </div>
      );
    }
  };

  // Calls right when the page renders.
  // These functions are being called now so that they don't get called each time the tabs are clicked.
  React.useEffect(() => {
    if (resourcesData) {
      const rs = GetResourcesResponse.fromJSON(resourcesData);
      const u = GetUsersResponse.fromJSON(sphereOverview);

      // Storing the unique facility names upon calling the resource response.
      tabs(rs);

      // Getting a full, overall statistic of every resource that's available.
      gatherAllResources(rs);

      // Setting the JSON response so that it can be stored each time we call a tab.
      setProcessedData(rs);
    }
  }, [resourcesData]);

  // This is to compress all of the nodes before sending them to the ActionList.
  const compressNodes = (nodes: Allocation[]) => {
    // Defining a regular expression to catch anything that has a numerical value somewhere within the node names.
    const regexDigit = /(\D*)(\d+)(\D*)/;

    // This is a set which contains all of the unique experiment names (rid.eid.pid).
    const grouped = {};

    // Final list that we're returning.
    const expNodes = [];

    // Group every experiment into an object.
    for (const { rid, eid, pid, node } of nodes) {
      const key = `${rid}.${eid}.${pid}`;
      // If the current experiment doesn't exist, we will create an object for it.
      if (!grouped[key]) grouped[key] = [];
      // Then, we will push the current node to that object. (Associating nodes with experiments here).
      grouped[key].push(node);
    }

    // Now, each group is going to be processed. Iterate through all the experiments.
    for (const [expName, nodeList] of Object.entries(grouped)) {
      // This is going to be the list of node names in the resource page. Everything surrounded by the [ ].
      const nodeNames = [];
      // Storing non-numeric node names here temporarily. These are not iterable.
      let nonNumericGroup = [];

      // Some initializations before creating ranges for iterable nodes.
      let startNode = null;
      let prevNode = null;
      let startNum = null;
      let prevNum = null;
      let prevNodeNoNum = null;

      // This is called whenever we are at the end of a range of nodes.
      function closeRange() {
        // Ignore if startNode === null. Cannot create a range from it.
        if (!startNode) return;

        // If the range of values is itself, we will just add the starting node name.
        // Otherwise, we will append the string with the range.
        nodeNames.push(startNode === prevNode ? startNode : `${startNode}–${prevNode}`);

        // Reset the iterators.
        startNode = prevNode = null;
        startNum = prevNum = null;
        prevNodeNoNum = null;
      }

      // Here, we will push the non-numerical node names to nodeNames, then reset it.
      function flushNonNumericGroup() {
        if (nonNumericGroup.length > 0) {
          nodeNames.push(...nonNumericGroup);
          nonNumericGroup = [];
        }
      }

      // If the nodes have an iterator in it, this will sort the nodes numerically instead of lexicographically.
      // NOTE: This is just going to sort the elements with an iterator in them.
      // Example was followed here to construct this: https://stackoverflow.com/questions/5002848/how-to-define-custom-sort-function-in-javascript
      const sortedNodes = [...nodeList].sort((a, b) => {
        // Getting the numerical values of the node names with the regex pattern.
        const aMatch = a.match(regexDigit);
        const bMatch = b.match(regexDigit);

        // Checking that they exist (which they should) before sorting.
        if (aMatch && bMatch) {
          // Example: foo1bar
          // aMatch[0] = foo1bar (not needed -- thrown away)
          // aMatch[1] = foo (prefix)
          // aMatch[2] = 1 (NumStr)
          // aMatch[3] = bar (suffix)
          const [, aPrefix, aNumStr, aSuffix] = aMatch;
          const [, bPrefix, bNumStr, bSuffix] = bMatch;

          // Are the node names the same, minus the iterator? If so...
          if (aPrefix + aSuffix === bPrefix + bSuffix) {
            // Sort the numbers. Subtraction will sort in ascending order.
            return parseInt(aNumStr, 10) - parseInt(bNumStr, 10);
          }

          // The nodes are differently named. Sort them lexographically.
          return (aPrefix + aSuffix).localeCompare(bPrefix + bSuffix);
        }

        // The nodes do not have an iterator. Just sort them lexographically.
        return a.localeCompare(b);
      });

      // Iterating through all the nodes after sorting them.
      for (const node of sortedNodes) {
        // Attempt to capture any number inside of the nodes.
        const match = node.match(regexDigit);

        // Is there a number? If so...
        if (match) {
          // Get the full string, everything before/after the number, and the number itself.
          const [full, prefix, numStr, suffix] = match;
          // Cast the number as an integer for iterations, then get the name of the node without the number in it.
          // We are doing this to check when to break the iterations.
          const num = parseInt(numStr, 10);
          const nodeNoNum = prefix + suffix;

          // Check for discontinuity.
          if (
            nodeNoNum !== prevNodeNoNum || // Did we switch names of nodes? (foo1bar to bar2baz)
            num !== prevNum + 1 // Did the numbers change? (foo1bar to foo4bar)
          ) {
            // We will check if we have any stored, non-iterable nodes right now, then push them to the output.
            flushNonNumericGroup();
            // Then, close the range with our current startNode and prevNode values.
            closeRange();

            // Finally, set some iterators.
            if (!startNode && !startNum) {
              startNode = full;
              startNum = num;
            }

            prevNode = full;
            prevNum = num;
            prevNodeNoNum = nodeNoNum;
          }

          // Otherwise, we're still counting up. Set the iterators. Do not touch the "start" variables,
          // since we don't want to lose the starting ranges.
          else {
            prevNode = full;
            prevNum = num;
          }
        }

        // The node doesn't have a value to it. Close the range, if one exists. Push
        // the current node to the nonNumericGroup for when our next match occurs.
        else {
          closeRange();
          nonNumericGroup.push(node);
        }
      }

      // Iterations are complete. This does not add our final node range. Perform clean up.
      closeRange();
      flushNonNumericGroup();

      // Take all of the node names, then push them to an array for output.
      expNodes.push(`${expName}[${nodeNames.join(', ')}]`);
    }

    // Return what was generated above.
    return expNodes;
  };

  // Creating a mapper for displaying resources.
  const mapper = React.useCallback(() => {
    if (!processedData) return [];

    // Track unique experiments per facility.
    // Used to compute the "total activity" table.
    const expNodesByFacility = Object.fromEntries(facTabs.map((facility) => [facility, new Set()]));

    // Creating some initializations.
    const rows = [];
    let totalCores = 0;
    let totalMem = 0;
    let totalFreeCores = 0;
    let totalFreeMem = 0;

    // Iterating through all of the resources that we have.
    processedData.resources.forEach((r) => {
      const facility = r.resource?.facility || '';
      const resourceName = r.resource?.id || '';

      // This is to filter the results based on the current tab.
      if ((!facility || (activeTabKey && facility !== activeTabKey)) && activeTabKey !== 'All Facilities') {
        return;
      }

      // Calculating the total cores/memory for the current tab.
      let resourceCores = 0;
      let resourceMem = 0;
      let usedCores = 0;
      let usedMem = 0;

      r.resource?.procs?.forEach((p) => {
        resourceCores += p.cores;
        totalCores += p.cores;
      });

      r.resource?.memory?.forEach((m) => {
        resourceMem += m.capacity;
        totalMem += m.capacity;
      });

      r.allocated.forEach((a) => {
        usedCores += a.coresUsed;
        usedMem += a.memoryUsed;
      });

      // Take the allocated array (contains the experiments) and compress/sort them.
      const expNodes = compressNodes(r.allocated);

      // Collect unique experiment nodes per facility.
      if (expNodes.length !== 0) {
        expNodes.forEach((exp) => {
          // Strip the node name, so that it's just the base name.
          const baseExpName = exp.replace(/\[.*?\]/g, '');
          // Add it to the facility.
          expNodesByFacility[facility]?.add(baseExpName);
        });
      }

      // Creating the free cores/memory based on this resource.
      const freeCores = resourceCores - usedCores;
      const freeMem = resourceMem - usedMem;
      totalFreeCores += freeCores;
      totalFreeMem += freeMem;

      let mode = 'Idle';
      if (r.allocated.length === 0) {
        mode = 'Idle';
      } else if (r.allocated.length >= 2 || r.allocated.every((a) => a.virt)) {
        mode = 'Hypervisor';
      } else {
        mode = 'Bare Metal';
      }

      rows.push({
        facility,
        resource: (
          <>
            <Link to={`/resources/${resourceName}`}>{`${resourceName}`}</Link>
          </>
        ),
        allocation_mode: mode,
        experiment_nodes: expNodes.join(', '),
        free_cores: freeCores,
        free_memory: formatBytes(freeMem),
      });
    });

    // Count the total number of unique experiments per facility.
    const allExpNodes = Object.fromEntries(
      Object.entries(expNodesByFacility).map(([facility, expSet]) => [facility, expSet.size])
    );

    // Set the hook for current resources.
    setCurrentResources({
      cores: totalCores,
      memory: totalMem,
      freeCores: totalFreeCores,
      freeMemory: totalFreeMem,
    });

    // This filters rows based on "View All" or "View Only Available".
    const filteredRows = resourceView
      ? rows
      : rows.filter((row) => !(row.free_cores === 0 || parseInt(row.free_memory) === 0));

    return filteredRows;
  }, [activeTabKey, resourceView]);

  // Displaying the entire "resource" page here.
  return (
    <React.Fragment>
      {/* Creating the header. */}
      {header}
      <PageSection hasBodyWrapper={false}>
        <Card>
          <CardHeader style={{ fontWeight: 'bold' }}>Total Resources</CardHeader>
          {/* Creating the graphs. */}
          <CardBody>{createGraphs(currentResources, allResources, activeTabKey)}</CardBody>
        </Card>
      </PageSection>
      <PageSection hasBodyWrapper={false}>
        <Card style={{ marginBottom: '20px' }}>
          <CardHeader style={{ fontWeight: 'bold' }}>Facility Resources</CardHeader>
          <div>
            {/* Creating the tabs. */}
            <Tabs activeKey={activeTabKey} onSelect={handleTabClick}>
              {facTabs.map((facility, index) => (
                <Tab key={facility} eventKey={facility} title={<TabTitleText>{facility}</TabTitleText>} />
              ))}
            </Tabs>
          </div>
          <CardBody>
            <div style={{ overflowX: 'auto', marginTop: '16px' }}>
              {/* Creating the list of facilities and their resources. */}
              <ActionList
                kind="resources"
                columns={columns}
                rows={resourcesData}
                mapper={mapper}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onSearchResults={handleSearchResults}
                isCardView={false}
                reloadTrigger={resourceView ? 1 : 0}
                reload={resourceView ? 1 : 0}
              />
            </div>
          </CardBody>
        </Card>
      </PageSection>
    </React.Fragment>
  );
};

// Formatting the memory.
function formatBytes(bytes: number, decimals = 0) {
  if (!+bytes) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Rounding the decimal for any units more than TB.
  const dm = i >= 4 ? 2 : decimals;

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export { Resources };
